import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../../context/authcontext";
import { ProductServiceService, ProductServiceType } from "./productService.service";

interface ProductServiceFilterDropdownProps {
  onFilterChange: (typeFilter: string | null) => void;
  currentFilters: {
    searchTerm: string;
    dateRange: [Date | null, Date | null];
    typeFilter: string | null;
  };
  loading?: boolean;
}

const ProductServiceFilterDropdown: React.FC<ProductServiceFilterDropdownProps> = ({
  onFilterChange,
  currentFilters,
  loading = false,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [productServiceTypes, setProductServiceTypes] = useState<ProductServiceType[]>([]);
  const [isLoadingTypes, setIsLoadingTypes] = useState(false);
  const [expandedFilters, setExpandedFilters] = useState<Set<string>>(
    new Set()
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [typeFilter, setTypeFilter] = useState(currentFilters.typeFilter);

  useEffect(() => {
    setTypeFilter(currentFilters.typeFilter);
  }, [currentFilters.typeFilter]);

  const fetchProductServiceTypes = async () => {
    if (!selectedTenantId) return;

    setIsLoadingTypes(true);
    try {
      const types = await ProductServiceService.getProductServiceTypes();
      setProductServiceTypes(types);
    } catch (error) {
      console.error("Error fetching product service types:", error);
    } finally {
      setIsLoadingTypes(false);
    }
  };

  useEffect(() => {
    fetchProductServiceTypes();
  }, [selectedTenantId]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const toggleFilterExpansion = (filterKey: string) => {
    const newExpandedFilters = new Set(expandedFilters);
    if (newExpandedFilters.has(filterKey)) {
      newExpandedFilters.delete(filterKey);
    } else {
      newExpandedFilters.add(filterKey);
    }
    setExpandedFilters(newExpandedFilters);
  };

  const handleTypeChange = (selectedType: string | null) => {
    setTypeFilter(selectedType);
  };

  const handleInputKeyPress = (
    event: React.KeyboardEvent,
    filterKey: string
  ) => {
    if (event.key === "Enter") {
      const newExpandedFilters = new Set(expandedFilters);
      newExpandedFilters.delete(filterKey);
      setExpandedFilters(newExpandedFilters);
    }
  };

  const applyFilters = () => {
    onFilterChange(typeFilter);
    setIsOpen(false);
  };

  const resetFilters = () => {
    setTypeFilter(null);
    onFilterChange(null);
  };

  const hasFormChanges = typeFilter !== currentFilters.typeFilter;

  const getCurrentTypeName = () => {
    if (typeFilter === null) return "All Types";
    const type = productServiceTypes.find(
      (t) => t.product_n_service_type_display_name === typeFilter
    );
    return type?.product_n_service_type_display_name || "All Types";
  };

  return (
    <div className="position-relative" ref={dropdownRef}>
      <button
        type="button"
        className="btn btn-outline-light shadow dropdown-toggle"
        onClick={toggleDropdown}
      >
        <img
          src="/assets/img/icons/filter.svg"
          alt="Filter"
          className="me-1"
          style={{ width: "16px", height: "16px" }}
        />
        <span className="me-2">Filter</span>
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show"
          style={{
            width: "280px",
            maxHeight: "400px",
            zIndex: 9999,
            position: "absolute",
            top: "100%",
            right: "0",
            left: "auto",
            marginTop: "0.125rem",
            backgroundColor: "white",
            border: "1px solid rgba(0,0,0,.15)",
            borderRadius: "0.375rem",
            boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <div className="p-3 pb-0">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <img
                  src="/assets/img/icons/filter.svg"
                  alt="Filter"
                  className="me-1"
                  style={{ width: "16px", height: "16px" }}
                />
                Type Filter
              </h6>
            </div>
          </div>

          {/* Scrollable Filter Content */}
          <div
            className="px-3"
            style={{ maxHeight: "300px", overflowY: "auto", flex: 1 }}
          >
            <div className="space-y-2">
              {/* Type Filter */}
              <div className="border rounded">
                <button
                  type="button"
                  className="w-100 btn btn-link text-start text-decoration-none p-2 d-flex justify-content-between align-items-center"
                  onClick={() => toggleFilterExpansion("type")}
                  disabled={loading || isLoadingTypes}
                >
                  <span className="small text-dark">
                    <strong>Type</strong>
                  </span>
                  <div className="d-flex align-items-center">
                    {getCurrentTypeName() !== "All Types" && (
                      <span className="small text-muted me-2">
                        {getCurrentTypeName()}
                      </span>
                    )}
                    <i
                      className={`ti ti-chevron-${
                        expandedFilters.has("type") ? "up" : "down"
                      } small text-dark`}
                    ></i>
                  </div>
                </button>
                {expandedFilters.has("type") && (
                  <div className="p-2 border-top">
                    <select
                      className="form-select form-select-sm"
                      value={typeFilter || ""}
                      onChange={(e) =>
                        handleTypeChange(
                          e.target.value ? e.target.value : null
                        )
                      }
                      disabled={loading || isLoadingTypes}
                    >
                      <option value="">All Types</option>
                      {productServiceTypes.map((type) => (
                        <option
                          key={type.product_n_service_type_id}
                          value={type.product_n_service_type_display_name}
                        >
                          {type.product_n_service_type_display_name}
                        </option>
                      ))}
                    </select>
                    {isLoadingTypes && (
                      <div className="text-center mt-2">
                        <div
                          className="spinner-border spinner-border-sm text-primary"
                          role="status"
                        >
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          <br></br>

          {/* Action Buttons */}
          <div
            className="p-3 pt-2 border-top"
            style={{ backgroundColor: "white" }}
          >
            <div className="d-flex gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm flex-fill"
                onClick={resetFilters}
                disabled={loading}
              >
                <i className="ti ti-refresh me-1"></i>
                Reset
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm flex-fill"
                onClick={applyFilters}
                disabled={loading || !hasFormChanges}
              >
                <i className="ti ti-check me-1"></i>
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductServiceFilterDropdown;
